import ALogoSpinner from '@/components/atoms/ALogoSpinner.tsx'
import { IterationStatus, GunslingerJobStatus } from '@/const/const.ts'
import { cn } from '@/lib/utils.ts'
import { CheckCircle } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { setup, StateFrom } from 'xstate'
import { useActor } from '@xstate/react'
export default function IterationStartupSequence({
  currentStatus,
  currentVmStatus,
}: {
  currentStatus: IterationStatus
  currentVmStatus: GunslingerJobStatus
}) {
  const [state] = useStartupActor(currentStatus, currentVmStatus)

  const currStepIdx = useMemo(() => {
    if (state.value === 'RUNNING') {
      // if running, then all steps are done.
      return trackedStates.length
    }
    return trackedStates.findIndex(value => value.state === state.value) ?? -1
  }, [state.value])

  return (
    <div className="space-y-3">
      {trackedStates.slice(0, currStepIdx + 1).map((value, idx) => {
        return (
          <IterationChecklistItem key={value.status} done={idx < currStepIdx}>
            {idx < currStepIdx ? value.titleDone : value.titleInProgress}
          </IterationChecklistItem>
        )
      })}
    </div>
  )
}

function useStartupActor(status: IterationStatus, vmStatus: GunslingerJobStatus) {
  const initialStateRef = useRef({ status, vmStatus })
  const actor = useActor(machine, {
    snapshot: {
      value: computeCurrentState(initialStateRef.current.status, initialStateRef.current.vmStatus), // one of the states,
      context: {
        status: initialStateRef.current.status,
        vmStatus: initialStateRef.current.vmStatus,
      },
      children: {},
      status: 'active',
    },
  })

  const [_state, send] = actor

  useEffect(() => {
    send({ type: 'to', status, vmStatus })
  }, [status, vmStatus, send])

  return actor
}

function computeCurrentState(status: IterationStatus, vmStatus: GunslingerJobStatus): MachineState {
  if (isRequested(status, vmStatus)) {
    return 'RUN_REQUESTED'
  }
  if (isStarting(status, vmStatus)) {
    return 'STARTING'
  }
  if (isStartingAgents(status, vmStatus)) {
    return 'STARTING_AGENTS'
  }
  if (isBuildingDockerContainer(status, vmStatus)) {
    return 'BUILDING_DOCKER_CONTAINER'
  }
  if (isSyncingSourceCode(status, vmStatus)) {
    return 'SYNCING_SOURCE_CODE'
  }
  if (isRunning(status, vmStatus)) {
    return 'RUNNING'
  }
  if (isStoppedVm(status, vmStatus)) {
    return 'STOPPED_VM'
  }
  return 'RUNNING'
}

function isRequested(status: IterationStatus, _vmStatus: GunslingerJobStatus) {
  return status === 'RUN_REQUESTED'
}

function isStarting(_status: IterationStatus, vmStatus: GunslingerJobStatus) {
  return vmStatus === 'STARTING' || vmStatus === 'STARTED'
}

function isStartingAgents(status: IterationStatus, _vmStatus: GunslingerJobStatus) {
  return status === 'STARTING_UP' // ask Scott about this
}

function isBuildingDockerContainer(status: IterationStatus, _vmStatus: GunslingerJobStatus) {
  return status === 'BUILDING_DOCKER_CONTAINER'
}
function isSyncingSourceCode(status: IterationStatus, _vmStatus: GunslingerJobStatus) {
  return status === 'SYNCING_SOURCE_CODE'
}
function isRunning(status: IterationStatus, vmStatus: GunslingerJobStatus) {
  return vmStatus === 'RUNNING' && status === 'RUNNING'
}
function isStoppedVm(_status: IterationStatus, vmStatus: GunslingerJobStatus) {
  return vmStatus === 'STOPPED' || vmStatus === 'ERROR'
}

function IterationChecklistItem({ children, done }: { children: React.ReactNode; done: boolean }) {
  const Icon = done ? CheckCircle : ALogoSpinner

  return (
    <div
      className={cn(
        'flex translate-y-0 items-center space-x-2 text-sm text-muted-foreground',
        'opacity-100 transition-[opacity,transform] starting:translate-y-4 starting:opacity-0'
      )}
    >
      <Icon className="size-4" />
      <span className={cn(!done && 'text-base-foreground')}>{children}</span>
    </div>
  )
}

const trackedStates: {
  titleDone: string
  titleInProgress: string
  state: MachineState
}[] = [
  {
    titleDone: 'Requested Virtual Machine',
    titleInProgress: 'Requesting Virtual Machine',
    state: 'RUN_REQUESTED',
  },
  {
    titleDone: 'Started Virtual Machine',
    titleInProgress: 'Starting Virtual Machine',
    state: 'STARTING',
  },
  {
    titleDone: 'Started Agents',
    titleInProgress: 'Starting Agents',
    state: 'STARTING_AGENTS',
  },
  {
    titleDone: 'Built Docker Container',
    titleInProgress: 'Building Docker Container',
    state: 'BUILDING_DOCKER_CONTAINER',
  },
  {
    titleDone: 'Synced Source Code',
    titleInProgress: 'Syncing Source Code',
    state: 'SYNCING_SOURCE_CODE',
  },
]

export const machine = setup({
  types: {
    context: {} as { status: IterationStatus; vmStatus: GunslingerJobStatus },
    events: {} as { type: 'to'; status: IterationStatus; vmStatus: GunslingerJobStatus },
  },
}).createMachine({
  context: {} as { status: IterationStatus; vmStatus: GunslingerJobStatus },
  id: 'IterationStartupStatus',
  initial: 'RUN_REQUESTED',
  states: {
    RUN_REQUESTED: {
      on: {
        to: {
          target: 'STARTING',
          guard: ({ context, event }) => {
            return isStarting(event.status, event.vmStatus)
          },
        },
      },
    },
    STARTING: {
      on: {
        to: {
          target: 'STARTING_AGENTS',
          guard: ({ context, event }) => {
            return isStartingAgents(event.status, event.vmStatus)
          },
        },
      },
    },
    STARTING_AGENTS: {
      on: {
        to: {
          target: 'BUILDING_DOCKER_CONTAINER',
          guard: ({ context, event }) => {
            return isBuildingDockerContainer(event.status, event.vmStatus)
          },
        },
      },
    },
    BUILDING_DOCKER_CONTAINER: {
      on: {
        to: {
          target: 'SYNCING_SOURCE_CODE',
          guard: ({ context, event }) => {
            return isSyncingSourceCode(event.status, event.vmStatus)
          },
        },
      },
    },
    SYNCING_SOURCE_CODE: {
      on: {
        to: {
          target: 'RUNNING',
          guard: ({ context, event }) => {
            return isRunning(event.status, event.vmStatus)
          },
        },
      },
    },
    RUNNING: {
      on: {
        to: {
          target: 'STOPPED_VM',
          guard: ({ context, event }) => {
            return isStoppedVm(event.status, event.vmStatus)
          },
        },
      },
    },

    STOPPED_VM: {
      on: {
        to: {
          target: 'RUN_REQUESTED',
        },
      },
    },
  } as const,
})

type MachineState = StateFrom<typeof machine>['value']
